import React from 'react';
import camelcase from 'camelcase-es5';

import useFormControlReducer from '../../hooks/useFormControlReducer';

import InputLabel from '../InputLabel';

import TextFieldFormControl from './TextFieldFormControl';
import TextFieldInput from './TextFieldInput';
import TextFieldFooter from './TextFieldFooter';
import TextFieldBase from './TextFieldBase';
import TTextField from './@types/TextField';
import generateTextFieldIds, { isCharsExceeded } from './generateTextFieldIds';

/**
 * input type=[text, password, email, number, tel, url] abstraction
 */
const TextField = ({
  labelText = '',
  optionalText,
  errorText,
  charsRemainingText,
  charsExceededText,
  helperText,
  inputRef,
  isRequired = false,
  onChange,
  onBlur,
  onFocus,
  onMouseOut,
  onMouseOver,
  onKeyPress,
  value,
  type = 'text',
  id = `TextField-${camelcase(labelText)}`,
  maxLength = 0,
  step,
  autoComplete = 'off',
  min,
  max,
  name,
  placeholder,
  inputMode,
  dataTestId,
  dataRef,
  dataValid,
  disabled,
  ariaLabel,
  ariaLive,
  isLive = true,
}: TTextField) => {
  const { state, handlers } = useFormControlReducer({
    value,
    errorText,
    onBlur,
    onFocus,
    onChange,
    onMouseOut,
    onMouseOver,
  });
  const { isDirty, isEmpty, isErrored, isFocused, characterCount } = state;
  const { handleChange, handleBlur, handleFocus, handleMouseOver, handleMouseOut } = handlers;

  const charsExceeded = isCharsExceeded(characterCount, maxLength);
  const { helperTextId, errorTextId, charsExceededId } = generateTextFieldIds(id, Boolean(errorText), charsExceeded);

  return (
    <TextFieldBase ariaLive={ariaLive}>
      {labelText && (
        <InputLabel htmlFor={id} isInputRequired={isRequired} labelText={labelText} optionalText={optionalText} />
      )}
      <TextFieldFormControl
        isDirty={isDirty}
        isErrored={isErrored || charsExceeded}
        isFocused={isFocused}
        isEmpty={isEmpty}
      >
        <TextFieldInput
          type={type}
          id={id}
          name={name}
          ref={inputRef}
          isRequired={isRequired}
          value={value}
          step={step}
          autoComplete={autoComplete}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          onKeyPress={onKeyPress}
          min={min}
          max={max}
          inputMode={inputMode}
          placeholder={placeholder}
          data-testid={dataTestId}
          data-ref={dataRef}
          data-valid={dataValid}
          disabled={disabled}
          ariaLabel={ariaLabel}
          helperTextId={helperTextId}
          errorTextId={errorTextId}
          charsExceededId={charsExceededId}
        />
      </TextFieldFormControl>

      <TextFieldFooter
        errorText={errorText}
        charsRemainingText={charsRemainingText}
        charsExceededText={charsExceededText}
        maxLength={maxLength}
        characterCount={characterCount}
        isFocused={isFocused}
        isRequired={isRequired}
        helperText={helperText}
        isLive={isLive}
        id={helperTextId}
        errorTextId={errorTextId}
        charsExceededId={charsExceededId}
        charsExceeded={charsExceeded}
      />
    </TextFieldBase>
  );
};

export default TextField;
